import React, { useMemo, useState } from "react";
import { Badge, Card, Col, Form, Row, Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons";
import HelpTip from "app/orderTool/components/HelpTip";
import { centimesToCurrency } from "helper/globalHelper";
import {
  computeResolvedCost,
  resolveGroupForDeliveryDate,
  resolvePackageForDeliveryDate,
  resolveSlotEntryWithModeOn,
  RESOLUTION_MODE_LABELS,
} from "helper/packageRotationHelper";
import { frDate, sonataGroupEditUrl, sonataPackageEditUrl, todayYmd } from "./slotLabels";

const TYPE_LABELS = { breakfast: "PDJ", caterer: "Cocktail", mealtray: "Plateaux" };
const MAX_CARDS_PER_SECTION = 60;

/**
 * Vue « Carte à une date » : toute la carte pour une date de livraison donnée —
 * les forfaits et les groupes / plateaux-repas avec leur composition effective
 * (lignes fixes + slots résolus par le calendrier), plus les produits portés
 * par un slot qui n'est rattaché à aucun forfait ni groupe. Chaque fiche
 * affiche sa rentabilité indicative (coût matière depuis les prix d'achat des
 * fiches produits, comme le récap marge de l'éditeur). Purement informatif.
 */
const ForfaitsAtDate = ({ packages, slots, productGroups = [], products = [], productParts = [] }) => {
  const [date, setDate] = useState(todayYmd());
  const [search, setSearch] = useState("");
  const [slotsOnly, setSlotsOnly] = useState(false);
  const [activeOnly, setActiveOnly] = useState(true);
  // Type de client : la carte standard (hors AO), la carte des appels d'offre, ou tout
  const [tenderFilter, setTenderFilter] = useState("hors_ao"); // 'hors_ao' | 'ao' | 'all'

  const productsById = useMemo(() => {
    const map = {};
    (products || []).forEach((p) => { map[p.id] = p; });
    return map;
  }, [products]);
  const partsById = useMemo(() => {
    const map = {};
    (productParts || []).forEach((p) => { map[p.id] = p; });
    return map;
  }, [productParts]);

  const costOf = (ref) => computeResolvedCost(ref, productsById, partsById);

  const filterAndResolve = (list, resolver) => {
    const needle = search.trim().toLowerCase();
    return [...list]
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
      .filter((p) => !activeOnly || p.active !== false)
      .filter((p) =>
        tenderFilter === "all" ||
        (tenderFilter === "ao" ? !!p.in_tender_catalog : !p.in_tender_catalog)
      )
      .filter((p) => !slotsOnly || (p.slot_lines || []).length > 0)
      .filter((p) => !needle || (p.name || "").toLowerCase().includes(needle))
      .map((p) => resolver(p, date, slots) || p);
  };

  const resolved = useMemo(
    () => filterAndResolve(packages, resolvePackageForDeliveryDate),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [packages, slots, date, search, slotsOnly, activeOnly, tenderFilter]
  );
  const resolvedGroups = useMemo(
    () => filterAndResolve(productGroups, resolveGroupForDeliveryDate),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [productGroups, slots, date, search, slotsOnly, activeOnly, tenderFilter]
  );

  // Produits de la carte hors fiches : slots rattachés à aucun forfait ni
  // groupe, résolus à la date — ils font partie de la carte mais ne se voient
  // dans aucune fiche ci-dessus.
  const unattachedSlotRows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return [...slots]
      .filter((s) => !(s.package_lines || []).length && !(s.group_lines || []).length)
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
      .map((slot) => {
        const { entry, mode } = resolveSlotEntryWithModeOn(slot, date);
        const isPart = slot.item_type === "product_part";
        const item = entry
          ? (isPart ? partsById[entry.item_id] : productsById[entry.item_id]) || null
          : null;
        const parent = isPart && item ? productsById[item.product_id] : null;
        const sale = !isPart && item && item.price !== null && item.price !== undefined ? Number(item.price) : null;
        const purchase = isPart
          ? parent && parent.purchase_price_ht !== null && parent.purchase_price_ht !== undefined
            ? Math.round(Number(parent.purchase_price_ht) / (Number(entry?.parts_per_product || item?.parts_per_product) || 1))
            : null
          : item && item.purchase_price_ht !== null && item.purchase_price_ht !== undefined
            ? Number(item.purchase_price_ht)
            : null;
        const margin = sale !== null && purchase !== null ? sale - purchase : null;
        return { slot, entry, mode, isPart, sale, purchase, margin };
      })
      .filter((r) => !needle
        || (r.slot.name || "").toLowerCase().includes(needle)
        || (r.entry?.name || "").toLowerCase().includes(needle));
  }, [slots, date, search, productsById, partsById]);

  const nbWithSlots =
    packages.filter((p) => (p.slot_lines || []).length > 0).length +
    productGroups.filter((g) => (g.slot_lines || []).length > 0).length;

  const renderLine = (line, idKey, i) => (
    <li key={`${idKey}_${line[idKey]}_${i}`} className={line._slot_name ? "text-primary" : ""}>
      {line.quantity != null ? `${line.quantity} × ` : ""}
      {line.name || `#${line[idKey]}`}
      {line._slot_name && (
        <Badge variant="info" className="ml-1" style={{ fontSize: "0.62rem" }}>
          {line._slot_name}
        </Badge>
      )}
      {RESOLUTION_MODE_LABELS[line._resolution_mode] && (
        <Badge variant="secondary" className="ml-1" style={{ fontSize: "0.62rem" }}>
          {RESOLUTION_MODE_LABELS[line._resolution_mode]}
        </Badge>
      )}
    </li>
  );

  const renderMargin = (ref) => {
    const sale = ref.price !== null && ref.price !== undefined ? Number(ref.price) : null;
    const c = costOf(ref);
    if (sale === null && !c) return null;
    const margin = sale !== null && c ? sale - c.cost : null;
    const rate = margin !== null && sale > 0 ? (margin / sale) * 100 : null;
    return (
      <div
        className="border-top mt-2 pt-1 d-flex justify-content-between text-nowrap"
        style={{ fontSize: "0.72rem" }}
      >
        <span title="Prix de vente HT de la fiche">
          Vente {sale !== null ? centimesToCurrency(sale) : "—"}
        </span>
        <span title={c?.manual ? "Prix d'achat saisi sur la fiche" : "Somme des prix d'achat des produits composants à cette date"}>
          Coût {c ? centimesToCurrency(c.cost) : "—"}
          {c && !c.complete && <span style={{ color: "#b8860b" }}> ⚠</span>}
        </span>
        <span
          title="Marge HT (taux sur le prix de vente)"
          style={{ color: margin !== null && margin < 0 ? "#c0392b" : "inherit", fontWeight: 600 }}
        >
          Marge {margin !== null ? centimesToCurrency(margin) : "—"}
          {rate !== null ? ` (${(Math.round(rate * 10) / 10).toFixed(1)} %)` : ""}
        </span>
      </div>
    );
  };

  const renderCard = (p, isGroup) => {
    const hasSlots = (p.slot_lines || []).length > 0;
    const empty = !(p.products || []).length && !(p.product_parts || []).length;
    return (
      <Col key={`${isGroup ? "g" : "p"}_${p.id}`} md={6} lg={4} className="mb-3 d-flex">
        <Card className="w-100" border={hasSlots ? "info" : undefined}>
          <Card.Header className="py-2 d-flex justify-content-between align-items-start">
            <strong style={{ fontSize: "0.85rem" }}>
              {p.name || `#${p.id}`}
              <a
                href={isGroup ? sonataGroupEditUrl(p.id) : sonataPackageEditUrl(p.id)}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-2"
                title={`Ouvrir la fiche Sonata du ${isGroup ? "groupe" : "forfait"}`}
              >
                <FontAwesomeIcon icon={faExternalLinkAlt} style={{ fontSize: "0.7rem" }} />
              </a>
            </strong>
            <span className="text-nowrap ml-2">
              {p.in_tender_catalog && (
                <Badge variant="warning" className="ml-1">AO</Badge>
              )}
              {p.active === false && (
                <Badge variant="secondary" className="ml-1">inactif</Badge>
              )}
              {(p.types || []).map((t) => (
                <Badge key={t} variant="light" className="ml-1 border">
                  {TYPE_LABELS[t] || t}
                </Badge>
              ))}
            </span>
          </Card.Header>
          <Card.Body className="py-2" style={{ fontSize: "0.8rem" }}>
            {(p._unresolvedSlots || []).map((u) => (
              <div key={u.slot_id} className="text-danger">
                ⚠ {u.slot_name} : non résolu à cette date
              </div>
            ))}
            {empty && !(p._unresolvedSlots || []).length ? (
              <span className="text-muted">Aucune composition.</span>
            ) : (
              <ul className="list-unstyled mb-0">
                {(p.products || []).map((line, i) => renderLine(line, "product_id", i))}
                {(p.product_parts || []).map((line, i) => renderLine(line, "product_part_id", i))}
              </ul>
            )}
            {renderMargin(p)}
          </Card.Body>
        </Card>
      </Col>
    );
  };

  const renderSection = (title, list, isGroup) => (
    <>
      <h5 className="mt-2 mb-2" style={{ fontSize: "1rem" }}>
        {title} <span className="text-muted">({list.length})</span>
      </h5>
      {list.length === 0 ? (
        <p className="text-muted">Aucune fiche ne correspond.</p>
      ) : (
        <>
          <Row>{list.slice(0, MAX_CARDS_PER_SECTION).map((p) => renderCard(p, isGroup))}</Row>
          {list.length > MAX_CARDS_PER_SECTION && (
            <p className="text-muted">
              {list.length - MAX_CARDS_PER_SECTION} fiche(s) non affichée(s) — affinez la
              recherche ou les filtres.
            </p>
          )}
        </>
      )}
    </>
  );

  return (
    <div>
      <Row className="align-items-end mb-3">
        <Col md="auto">
          <Form.Label className="mb-1 small font-weight-bold">
            Date de livraison
            <HelpTip title="Carte à une date" placement="right">
              <p className="mb-1">
                Toute la carte pour cette date : les <strong>forfaits</strong> et les{" "}
                <strong>groupes / plateaux-repas</strong> avec leur composition{" "}
                <strong>effective</strong> (lignes fixes + slots résolus par leur calendrier, en{" "}
                <span className="text-primary">bleu</span> avec le badge du slot), plus les{" "}
                <strong>produits au slot</strong> qui ne sont dans aucune fiche.
              </p>
              <p className="mb-1">
                La ligne <strong>Vente / Coût / Marge</strong> est indicative : coût matière =
                prix d'achat saisi sur la fiche, sinon somme des prix d'achat des produits
                composants à cette date (⚠ = des composants sans prix d'achat manquent au
                calcul).
              </p>
              <p className="mb-0">
                <strong>Type de client</strong> : <Badge variant="warning">AO</Badge> = présent
                dans un catalogue d'appel d'offre — « Hors AO » (défaut) = la carte standard,
                « Client AO » = uniquement les fiches d'appels d'offre, « Tous » = les deux.
                La section « Produits au slot » n'est pas concernée. Un ⚠ rouge signale un slot
                sans entrée au calendrier à cette date (la ligne serait omise).
              </p>
            </HelpTip>
          </Form.Label>
          <Form.Control type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </Col>
        <Col md={3}>
          <Form.Label className="mb-1 small font-weight-bold">Recherche</Form.Label>
          <Form.Control
            type="text"
            placeholder="Nom du forfait, du groupe, du slot…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Col>
        <Col md="auto" className="pb-2">
          <Form.Check
            type="checkbox"
            id="forfaits-active-only"
            label="Actifs uniquement"
            checked={activeOnly}
            onChange={(e) => setActiveOnly(e.target.checked)}
          />
        </Col>
        <Col md="auto" className="pb-2">
          <Form.Label className="mb-1 small font-weight-bold d-block">Type de client</Form.Label>
          <Form.Check
            inline
            type="radio"
            id="carte-tender-hors-ao"
            label="Hors AO"
            checked={tenderFilter === "hors_ao"}
            onChange={() => setTenderFilter("hors_ao")}
          />
          <Form.Check
            inline
            type="radio"
            id="carte-tender-ao"
            label="Client AO"
            checked={tenderFilter === "ao"}
            onChange={() => setTenderFilter("ao")}
          />
          <Form.Check
            inline
            type="radio"
            id="carte-tender-all"
            label="Tous"
            checked={tenderFilter === "all"}
            onChange={() => setTenderFilter("all")}
          />
        </Col>
        <Col md="auto" className="pb-2">
          <Form.Check
            type="checkbox"
            id="forfaits-slots-only"
            label={`Seulement les fiches à slots (${nbWithSlots})`}
            checked={slotsOnly}
            onChange={(e) => setSlotsOnly(e.target.checked)}
          />
        </Col>
        <Col className="text-right text-muted small pb-2">
          {resolved.length} forfait(s) + {resolvedGroups.length} groupe(s) +{" "}
          {unattachedSlotRows.length} slot(s) hors fiches au {frDate(date)}
        </Col>
      </Row>

      {renderSection("Forfaits", resolved, false)}
      {renderSection("Groupes / plateaux-repas", resolvedGroups, true)}

      <h5 className="mt-2 mb-2" style={{ fontSize: "1rem" }}>
        Produits au slot — hors forfaits &amp; groupes{" "}
        <span className="text-muted">({unattachedSlotRows.length})</span>
        <HelpTip title="Slots hors fiches" placement="right">
          <p className="mb-0">
            Les slots qui ne sont rattachés à aucun forfait ni groupe : leur produit fait
            partie de la carte à cette date mais n'apparaît dans aucune fiche ci-dessus.
            Vente = prix de la fiche produit, coût = son prix d'achat (part : prix du produit
            parent ÷ nb de parts).
          </p>
        </HelpTip>
      </h5>
      {unattachedSlotRows.length === 0 ? (
        <p className="text-muted mb-0">
          Aucun slot hors fiches{search.trim() ? " ne correspond" : ""}.
        </p>
      ) : (
        <Table striped hover size="sm" style={{ fontSize: "0.8rem" }}>
          <thead>
            <tr>
              <th>Slot</th>
              <th>Produit au {frDate(date)}</th>
              <th className="text-right">Vente HT</th>
              <th className="text-right">Coût</th>
              <th className="text-right">Marge</th>
            </tr>
          </thead>
          <tbody>
            {unattachedSlotRows.map(({ slot, entry, mode, isPart, sale, purchase, margin }) => (
              <tr key={slot.id}>
                <td>
                  {slot.name}
                  {isPart && <Badge variant="secondary" className="ml-1">part</Badge>}
                </td>
                <td>
                  {entry ? (
                    <span className="text-primary">
                      {entry.name || `#${entry.item_id}`}
                      {RESOLUTION_MODE_LABELS[mode] && (
                        <Badge variant="secondary" className="ml-1" style={{ fontSize: "0.62rem" }}>
                          {RESOLUTION_MODE_LABELS[mode]}
                        </Badge>
                      )}
                    </span>
                  ) : (
                    <span className="text-danger">⚠ non résolu à cette date</span>
                  )}
                </td>
                <td className="text-right">{sale !== null ? centimesToCurrency(sale) : "—"}</td>
                <td className="text-right">{purchase !== null ? centimesToCurrency(purchase) : "—"}</td>
                <td
                  className="text-right"
                  style={{ color: margin !== null && margin < 0 ? "#c0392b" : "inherit", fontWeight: margin !== null ? 600 : undefined }}
                >
                  {margin !== null
                    ? `${centimesToCurrency(margin)}${sale > 0 ? ` (${(Math.round(((margin / sale) * 100) * 10) / 10).toFixed(1)} %)` : ""}`
                    : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
};

export default ForfaitsAtDate;
